import { virtualColor } from '@mantine/core';
import type { MantineColorsTuple} from '@mantine/core';

// Color Tokens
export const colors = {
  // Primary brand colors - Soft Teal (#5fb3b3)
  // UX Spec: Primary color palette - Soft Teal as primary with proper shade scale
  primary: [
    'oklch(98% 0.02 197)', // Lightest
    'oklch(95% 0.04 197)',
    'oklch(90% 0.06 197)',
    'oklch(82% 0.09 197)',
    'oklch(74% 0.11 197)',
    'oklch(68% 0.12 197)', // Base color (#5fb3b3) - index 6
    'oklch(62% 0.11 197)',
    'oklch(56% 0.10 197)',
    'oklch(50% 0.09 197)',
    'oklch(44% 0.08 197)', // Darkest
  ] as MantineColorsTuple,

  // Secondary brand colors - Soft Lavender (#9b7ede)
  // UX Spec: Accent color - Soft Lavender for highlights and special features
  secondary: [
    'oklch(96% 0.05 305)', // Lightest
    'oklch(90% 0.10 305)',
    'oklch(82% 0.15 305)',
    'oklch(72% 0.18 305)',
    'oklch(65% 0.20 305)',
    'oklch(59% 0.20 305)', // Base color (#9b7ede) - index 6
    'oklch(53% 0.18 305)',
    'oklch(47% 0.16 305)',
    'oklch(41% 0.14 305)',
    'oklch(35% 0.12 305)', // Darkest
  ] as MantineColorsTuple,

  // Accent color - Soft Lavender (#9b7ede)
  // UX Spec: Accent color for highlights and special features
  accent: [
    'oklch(96% 0.05 305)', // Lightest
    'oklch(90% 0.10 305)',
    'oklch(82% 0.15 305)',
    'oklch(72% 0.18 305)',
    'oklch(65% 0.20 305)',
    'oklch(59% 0.20 305)', // Base color (#9b7ede) - index 6
    'oklch(53% 0.18 305)',
    'oklch(47% 0.16 305)',
    'oklch(41% 0.14 305)',
    'oklch(35% 0.12 305)', // Darkest
  ] as MantineColorsTuple,

  // Virtual colors for light/dark themes
  // UX Spec: Light backgrounds (#ffffff, #f8f9fa), Dark backgrounds (#0f0f0f, #1a1a1a)
  // UX Spec: Light text (#212529), Dark text (#e0e0e0 - white)
  body: virtualColor({
    name: 'body',
    light: 'lightBg', // Light: #ffffff (lightBg[6] - white background)
    dark: 'darkBg', // Dark: oklch(22% 0 0) (darkBg[6] - virtualColor uses shade 6)
  }),

  surface: virtualColor({
    name: 'surface',
    light: 'lightBg', // Light: #ffffff (lightBg[6] - white background)
    dark: 'darkSurface', // Dark: oklch(36% 0 0) (darkSurface[6] - lighter than body 22%)
  }),

  border: virtualColor({
    name: 'border',
    light: 'info',
    dark: 'neutral',
  }),

  text: virtualColor({
    name: 'text',
    light: 'dark', // Light: #212529 (dark[6] - dark text on light background)
    dark: 'textDark', // Dark: #e0e0e0 (textDark[6] - white text on dark background)
  }),

  muted: virtualColor({
    name: 'muted',
    light: 'neutral', // Light: #495057 (neutral[5])
    dark: 'textDark', // Dark: #a0a0a0 (textDark[2])
  }),

  subtle: virtualColor({
    name: 'subtle',
    light: 'neutral', // Light: #6c757d (neutral[4])
    dark: 'textDark', // Dark: #888888 (textDark[0])
  }),

  disabled: virtualColor({
    name: 'disabled',
    light: 'neutral',
    dark: 'darkBg',
  }),

  // Sparkle indicator color (amber/gold) for delta changes
  // UX Spec: Visual feedback for value changes - warm amber/gold color
  sparkle: virtualColor({
    name: 'sparkle',
    light: 'warning', // amber/gold palette
    dark: 'warning',
  }),

  // Base vs. personalized estimate comparison (Story 2.10) — dimmed secondary line in header
  'base-estimate': virtualColor({
    name: 'base-estimate',
    light: 'neutral',
    dark: 'textDark',
  }),

  // Semantic colors
  // Success - Sage Green (#7fb069)
  // UX Spec: Success (Sage Green #7fb069) for success states, positive feedback
  success: [
    'oklch(96% 0.05 133)', // Lightest
    'oklch(91% 0.08 133)',
    'oklch(85% 0.12 133)',
    'oklch(77% 0.16 133)',
    'oklch(70% 0.18 133)',
    'oklch(67% 0.15 133)', // Base color (#7fb069) - index 6
    'oklch(60% 0.13 133)',
    'oklch(54% 0.12 133)',
    'oklch(48% 0.11 133)',
    'oklch(42% 0.10 133)', // Darkest
  ] as MantineColorsTuple,

  // Warning - Orange/Amber (#f59e0b)
  // UX Spec: Warning (#f59e0b) for important notices, upcoming deadlines
  warning: [
    'oklch(98% 0.05 73)', // Lightest
    'oklch(95% 0.08 73)',
    'oklch(90% 0.12 73)',
    'oklch(82% 0.15 73)',
    'oklch(76% 0.15 73)',
    'oklch(72% 0.15 73)', // Base color (#f59e0b) - index 6
    'oklch(66% 0.14 73)',
    'oklch(60% 0.13 73)',
    'oklch(54% 0.12 73)',
    'oklch(48% 0.11 73)', // Darkest
  ] as MantineColorsTuple,

  // Error - Red (#ef4444)
  // UX Spec: Error (#ef4444) for validation errors, critical issues
  error: [
    'oklch(96% 0.05 31)', // Lightest
    'oklch(91% 0.08 31)',
    'oklch(85% 0.12 31)',
    'oklch(77% 0.16 31)',
    'oklch(68% 0.19 31)',
    'oklch(55% 0.20 31)', // Base color (#ef4444) - index 6
    'oklch(50% 0.18 31)',
    'oklch(45% 0.16 31)',
    'oklch(40% 0.14 31)',
    'oklch(35% 0.12 31)', // Darkest
  ] as MantineColorsTuple,

  // Info - Soft Teal (same as primary #5fb3b3)
  // UX Spec: Info (Soft Teal #5fb3b3) for informational messages, tips, suggestion cards
  info: [
    'oklch(98% 0.02 197)', // Lightest
    'oklch(95% 0.04 197)',
    'oklch(90% 0.06 197)',
    'oklch(82% 0.09 197)',
    'oklch(74% 0.11 197)',
    'oklch(68% 0.12 197)', // Base color (#5fb3b3) - index 6 (same as primary)
    'oklch(62% 0.11 197)',
    'oklch(56% 0.10 197)',
    'oklch(50% 0.09 197)',
    'oklch(44% 0.08 197)', // Darkest
  ] as MantineColorsTuple,

  // Light theme background colors
  // UX Spec: Light theme backgrounds (#ffffff, #f8f9fa, #e9ecef)
  // Array ordered so virtualColor (uses shade 6) = white background
  lightBg: [
    'oklch(93% 0 0)', // #e9ecef - Lightest (tertiary)
    'oklch(95% 0 0)',
    'oklch(97% 0 0)',
    'oklch(98% 0 0)', // #f8f9fa - Secondary background
    'oklch(99% 0 0)',
    'oklch(99.5% 0 0)',
    'oklch(100% 0 0)', // #ffffff - Light theme primary background (white) - index 6 for virtualColor
    'oklch(100% 0 0)', // #ffffff
    'oklch(100% 0 0)', // #ffffff
    'oklch(100% 0 0)', // #ffffff
  ] as MantineColorsTuple,

  // Mantine's default gray scale - override for WCAG AA compliance
  // These are used by c="dimmed" and other Mantine components
  gray: [
    'oklch(98% 0 0)',   // gray.0 - lightest
    'oklch(96% 0 0)',   // gray.1
    'oklch(93% 0 0)',   // gray.2
    'oklch(88% 0 0)',   // gray.3 - borders
    'oklch(75% 0 0)',   // gray.4
    'oklch(60% 0 0)',   // gray.5
    'oklch(48% 0 0)',   // gray.6 - WCAG AA compliant on white (7:1)
    'oklch(42% 0 0)',   // gray.7 - dimmed text (better contrast)
    'oklch(35% 0 0)',   // gray.8 - darker text
    'oklch(25% 0 0)',   // gray.9 - darkest
  ] as MantineColorsTuple,

  // Mantine's default dark scale - override for dark mode
  dark: [
    'oklch(95% 0 0)',   // dark.0 - lightest (for dark mode text)
    'oklch(90% 0 0)',   // dark.1
    'oklch(80% 0 0)',   // dark.2 - dimmed in dark mode
    'oklch(70% 0 0)',   // dark.3
    'oklch(60% 0 0)',   // dark.4
    'oklch(50% 0 0)',   // dark.5
    'oklch(36% 0 0)',   // dark.6 - surface
    'oklch(28% 0 0)',   // dark.7
    'oklch(22% 0 0)',   // dark.8 - body
    'oklch(16% 0 0)',   // dark.9 - darkest
  ] as MantineColorsTuple,

  // Neutral colors (grayscale) - for text and borders
  // UX Spec: Light theme text (#212529, #495057, #6c757d), borders (#dee2e6)
  neutral: [
    'oklch(100% 0 0)', // #ffffff - Lightest
    'oklch(98% 0 0)', // #f8f9fa
    'oklch(93% 0 0)', // #e9ecef
    'oklch(88% 0 0)', // #dee2e6 - Light theme border
    'oklch(75% 0 0)', // #6c757d - Light theme secondary text
    'oklch(65% 0 0)', // #495057 - Light theme muted text
    'oklch(25% 0 0)', // #212529 - Light theme primary text
    'oklch(20% 0 0)', // Dark theme border #2a2a2a
    'oklch(15% 0 0)', // #1a1a1a - Dark theme secondary background / surface
    'oklch(8% 0 0)', // #0f0f0f - Dark theme primary background
  ] as MantineColorsTuple,


  // Dark theme body background color
  // UX Spec: Dark theme primary background - lighter for better usability and contrast
  // Array ordered so virtualColor (uses shade 6) = 22% lightness background
  darkBg: [
    'oklch(10% 0 0)', // Darkest
    'oklch(12% 0 0)',
    'oklch(14% 0 0)',
    'oklch(16% 0 0)',
    'oklch(18% 0 0)',
    'oklch(20% 0 0)',
    'oklch(22% 0 0)', // Dark theme body background (22% lightness) - index 6 for virtualColor
    'oklch(24% 0 0)',
    'oklch(26% 0 0)',
    'oklch(28% 0 0)', // Lightest
  ] as MantineColorsTuple,

  // Dark theme surface color (cards, modals)
  // UX Spec: Dark theme secondary background / surface - significantly lighter than body for clear visual hierarchy
  // Array ordered so virtualColor (uses shade 6) = lighter surface with good contrast
  // Body is now 22% lightness, so surface should be noticeably lighter (36% lightness)
  darkSurface: [
    'oklch(22% 0 0)', // Darkest (matches body background)
    'oklch(24% 0 0)',
    'oklch(26% 0 0)',
    'oklch(28% 0 0)',
    'oklch(30% 0 0)',
    'oklch(32% 0 0)',
    'oklch(36% 0 0)', // Dark theme surface (36% lightness - lighter than body 22% for clear contrast) - index 6 for virtualColor
    'oklch(38% 0 0)',
    'oklch(40% 0 0)',
    'oklch(42% 0 0)', // Lightest
  ] as MantineColorsTuple,

  // Text colors for dark theme
  // UX Spec: Dark theme text (#e0e0e0, #a0a0a0, #888888)
  // Array ordered so virtualColor (uses shade 6) = white text (#e0e0e0)
  textDark: [
    'oklch(55% 0 0)', // #888888 - Dark theme secondary text
    'oklch(60% 0 0)',
    'oklch(65% 0 0)', // #a0a0a0 - Dark theme muted text
    'oklch(70% 0 0)',
    'oklch(75% 0 0)',
    'oklch(80% 0 0)',
    'oklch(88% 0 0)', // #e0e0e0 - Dark theme primary text (white) - index 6 for virtualColor
    'oklch(90% 0 0)',
    'oklch(95% 0 0)',
    'oklch(98% 0 0)',
  ] as MantineColorsTuple,

  // ============================================================================
  // ASSET FAMILY COLORS
  // ============================================================================
  // UX Spec: Consistent color coding for MAIN asset categories only
  // Main categories: Home, Vehicle, Pets, Self-Care (family/personal)
  // Used for: Task card left borders, asset badges, visual categorization
  // Design: Soft, calming colors that work in both light and dark modes
  // Note: Garden and Garage are child assets under Home, not separate root categories

  // Home - Royal Blue (#3B6FB8 → #5A8FD8)
  // Personality: Trustworthy, warm, primary living space
  // Includes: House, rooms, appliances, garden, garage, outdoor spaces
  assetHome: [
    'oklch(96% 0.04 250)', // Lightest
    'oklch(90% 0.08 250)',
    'oklch(82% 0.12 250)',
    'oklch(72% 0.14 250)',
    'oklch(62% 0.13 250)',
    'oklch(50% 0.12 250)', // Base color (#3B6FB8) - index 5
    'oklch(56% 0.13 250)', // Mid tone
    'oklch(64% 0.14 250)', // Light tone (#5A8FD8)
    'oklch(72% 0.14 250)',
    'oklch(80% 0.12 250)', // Lightest
  ] as MantineColorsTuple,

  // Vehicle - Slate Blue (#5B7C99 → #7B9CB9)
  // Personality: Reliable, mechanical, professional
  // Includes: Cars, trucks, motorcycles, vehicle maintenance
  assetVehicle: [
    'oklch(96% 0.03 240)', // Lightest
    'oklch(90% 0.05 240)',
    'oklch(82% 0.08 240)',
    'oklch(72% 0.09 240)',
    'oklch(62% 0.08 240)',
    'oklch(52% 0.07 240)', // Base color (#5B7C99) - index 5
    'oklch(58% 0.08 240)', // Mid tone
    'oklch(66% 0.09 240)', // Light tone (#7B9CB9)
    'oklch(74% 0.09 240)',
    'oklch(82% 0.08 240)', // Lightest
  ] as MantineColorsTuple,

  // Pets - Lime Green (#84CC16 → #A3E635)
  // Personality: Natural, caring, gentle
  // Includes: Dogs, cats, pet care, animal companions
  assetPets: [
    'oklch(96% 0.05 130)', // Lightest
    'oklch(90% 0.10 130)',
    'oklch(82% 0.15 130)',
    'oklch(74% 0.18 130)',
    'oklch(68% 0.18 130)',
    'oklch(64% 0.17 130)', // Base color (#84CC16) - index 5
    'oklch(70% 0.18 130)', // Mid tone
    'oklch(76% 0.19 130)', // Light tone (#A3E635)
    'oklch(82% 0.18 130)',
    'oklch(88% 0.15 130)', // Lightest
  ] as MantineColorsTuple,

  // Family - Soft Lavender (#9B7EDE → #BB9EFE)
  // Personality: Wellness, calm, personal, family care
  // Includes: Children, self-care, elderly dependents, family milestones
  assetFamily: [
    'oklch(96% 0.05 305)', // Lightest
    'oklch(90% 0.10 305)',
    'oklch(82% 0.15 305)',
    'oklch(72% 0.18 305)',
    'oklch(65% 0.20 305)',
    'oklch(59% 0.20 305)', // Base color (#9B7EDE) - index 5
    'oklch(65% 0.20 305)', // Mid tone
    'oklch(71% 0.20 305)', // Light tone (#BB9EFE)
    'oklch(77% 0.18 305)',
    'oklch(83% 0.15 305)', // Lightest
  ] as MantineColorsTuple,
};

// Typography Tokens
export const typography = {
  fontFamily: {
    sans: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif',
    mono: 'JetBrains Mono, Menlo, Monaco, Courier New, monospace',
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },
  fontWeights: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },
  lineHeights: {
    none: '1',
    tight: '1.25',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
  },
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
};

// Spacing Tokens
// UX Spec: 4px base unit system (xs: 4px, sm: 8px, md: 16px, lg: 24px, xl: 32px, 2xl: 48px, 3xl: 64px)
export const spacing = {
  none: '0',
  xs: '0.25rem', // 4px - UX Spec base unit
  sm: '0.5rem', // 8px
  md: '1rem', // 16px
  lg: '1.5rem', // 24px
  xl: '2rem', // 32px
  '2xl': '3rem', // 48px
  '3xl': '4rem', // 64px
};

// Border Radius Tokens
// UX Spec: radius.md = 8px (0.5rem) matches UX spec references
export const radius = {
  none: '0',
  xs: '0.125rem', // 2px
  sm: '0.25rem', // 4px
  md: '0.5rem', // 8px - UX Spec reference
  lg: '0.75rem', // 12px
  xl: '1rem', // 16px
  '2xl': '1.5rem', // 24px
  full: '9999px',
};

// Shadow Tokens
export const shadows = {
  none: 'none',
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  // Form specific shadows
  formInput: '0 3px 12px rgba(0, 0, 0, 0.1)',
  dropdown: '0 3px 12px rgba(0, 0, 0, 0.1)',
  formButton: '0 4px 12px rgba(0, 0, 0, 0.1)',
};

// Breakpoint Tokens
export const breakpoints = {
  xs: '36em', // 576px
  sm: '48em', // 768px
  md: '62em', // 992px
  lg: '75em', // 1200px
  xl: '88em', // 1408px
  '2xl': '96em', // 1536px
};

// Transition Tokens
export const transitions = {
  default: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  fast: '100ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  bounce: '200ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
};

// Z-Index Tokens
export const zIndices = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  modal: 400,
  popover: 500,
  overlay: 600,
  toast: 700,
  tooltip: 800,
};

// Container Tokens
export const containers = {
  xs: '100%',
  sm: '540px',
  md: '720px',
  lg: '960px',
  xl: '1140px',
  '2xl': '1320px',
  // Drawer width for medium+ screens
  drawer: '400px',
};

// Theme-level defaults (single source of truth for Mantine createTheme)
// Used for defaultRadius so unstyled components get consistent radius without local props.
export const themeDefaults = {
  defaultRadius: 'md',
} as const;

// Layout Tokens (misc sizes)
export const layout = {
  // Height of the fixed mobile footer / AppShell.Footer in px
  mobileFooterHeight: 48,
};

// Aspect Ratio Tokens
export const aspectRatios = {
  auto: 'auto',
  square: '1/1',
  video: '16/9',
  portrait: '3/4',
  landscape: '4/3',
};

/** Mantine `Badge` / `color` prop keys for DIY difficulty (palette-driven, theme-aware). */
export const diyDifficultyBadgeColor = {
  beginner: 'green',
  intermediate: 'yellow',
  advanced: 'red',
} as const;

// Export all tokens as a single object
export const tokens = {
  colors,
  typography,
  spacing,
  radius,
  shadows,
  breakpoints,
  transitions,
  zIndices,
  containers,
  layout,
  aspectRatios,
  diyDifficultyBadgeColor,
  themeDefaults,
} as const;

// Type for all tokens
export type Tokens = typeof tokens;
